import classNames from "classnames";
import CiroPill from "../../shared/CiroPill";
import CiroTooltipContainer from "../../shared/CiroTooltipContainer";
import CiroLink from "../../shared/CiroLink";
import { CheckIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { useContext } from "react";
import NavigationContext from "../../../contexts/NavigationContext";
import { getDomainFromUrl } from "../../../utils/formatters";

const AutopilotCriteriaMatchedCellInfo = ({
  scoringCriteriaResults,
  scoringCriteria,
  sources,
  changeBreakpointOnNavCollapse = true,
}: {
  scoringCriteriaResults: {
    matched_criteria?: boolean | null;
    scoring_criterion_id?: number | null;
  }[];
  scoringCriteria: {
    id: number;
    label: string;
    criterion: string;
  }[];
  sources: string[];
  changeBreakpointOnNavCollapse?: boolean;
}) => {
  const matchedCriteria =
    scoringCriteriaResults?.filter((result) => result.matched_criteria)
      .length ?? 0;
  const totalCriteria = scoringCriteriaResults?.length ?? 0;

  const { isNavCollapsed } = useContext(NavigationContext);
  const useLargerBreakpointClass =
    changeBreakpointOnNavCollapse && !isNavCollapsed;

  return (
    <>
      <div
        className={classNames(
          "hidden",
          useLargerBreakpointClass ? "2xl:flex" : "xl:flex",
          "flex-col",
          "items-center",
        )}
      >
        <CiroPill
          background={matchedCriteria === totalCriteria ? "bg-orange-100" : ""}
          customFontWeight="font-normal"
          fontColor="text-orange-600"
        >
          <span className="text-sm">
            {matchedCriteria} of {totalCriteria}
          </span>
        </CiroPill>
      </div>
      <div
        className={classNames(
          "hidden",
          useLargerBreakpointClass ? "max-2xl:flex" : "max-xl:flex",
          "flex-col",
          "items-center",
        )}
      >
        <CiroTooltipContainer
          tooltip={
            <div className={classNames("flex", "flex-col", "gap-2")}>
              {scoringCriteriaResults.map((result) => {
                const scoringCriterion = scoringCriteria.find(
                  (criterion) => criterion.id === result.scoring_criterion_id,
                );

                if (!scoringCriterion) {
                  return null;
                }

                return (
                  <div
                    key={result.scoring_criterion_id}
                    className={classNames(
                      "flex",
                      "flex-row",
                      "gap-2",
                      "items-center",
                    )}
                  >
                    {result.matched_criteria ? (
                      <div className={classNames("text-green-500")}>
                        <CheckIcon className="w-4 h-4" />
                      </div>
                    ) : (
                      <div className={classNames("text-red-500")}>
                        <XMarkIcon className="w-4 h-4" />
                      </div>
                    )}
                    <div
                      className={classNames({
                        "line-through": !result.matched_criteria,
                        "text-gray-500": !result.matched_criteria,
                      })}
                    >
                      {scoringCriterion.criterion}
                    </div>
                  </div>
                );
              })}
              {sources.length > 0 && (
                <div
                  className={classNames(
                    "flex",
                    "flex-col",
                    "gap-2",
                    "mt-2",
                    "pt-2",
                    "border-t",
                    "border-gray-200",
                  )}
                >
                  <div className={classNames("flex", "flex-wrap", "gap-1")}>
                    {sources.map((source) => (
                      <CiroLink
                        key={source}
                        href={source}
                        shouldOpenInNewTab={true}
                        unstyled={true}
                      >
                        <CiroPill
                          background="bg-gray-100"
                          fontColor="text-gray-600"
                          customFontWeight="font-normal"
                        >
                          {getDomainFromUrl(source)}
                        </CiroPill>
                      </CiroLink>
                    ))}
                  </div>
                </div>
              )}
            </div>
          }
        >
          <CiroPill
            background={
              matchedCriteria === totalCriteria ? "bg-orange-100" : ""
            }
            customFontWeight="font-normal"
            fontColor="text-orange-600"
          >
            <span className="text-sm">
              {matchedCriteria} of {totalCriteria}
            </span>
          </CiroPill>
        </CiroTooltipContainer>
      </div>
    </>
  );
};

export default AutopilotCriteriaMatchedCellInfo;
